import {Descriptions, Table, Tooltip} from 'antd';
import {
    formatToDollar,
    formatDate,
} from '../../../../helpers/genericHelper';
import {InfoCircleOutlined} from "@ant-design/icons";
import Data from '../../../CustomText/data';
import usePageState from '../../../../helpers/usePageState';

function CreditLimitIncentivesComponent({ accountId, creditLimitChanges = [], baseCreditLimit, domain }) {
    const [expandedRowKeys, setExpandedRowKeys] = usePageState(`credit-limit-rows:${accountId}`, ["0"]);

    // Define the columns for the table
    const columns = [
        {
            title: 'Date',
            dataIndex: 'updateTs',
            key: 'updateTs',
            render: (text) => formatDate(text, true),
        },
        {
            title: 'Credit Limit',
            dataIndex: 'creditLimitDollars',
            key: 'creditLimitDollars',
            render: (text) => formatToDollar(text),
        },
        {
            title: 'Trigger',
            dataIndex: 'triggerReason',
            key: 'triggerReason',
        },
        {
            title: 'Rule',
            dataIndex: 'rule',
            key: 'rule',
        },
        {
            title: (
                <>
                    Base Limit ID{' '}
                    <Tooltip title="Associated with a Plaid Account">
                        <InfoCircleOutlined />
                    </Tooltip>
                </>
            ),
            dataIndex: 'baseLimitId',
            key: 'baseLimitId',
        },
        {
            title: (
                <>
                    Bonus Given{' '}
                    <Tooltip title="Bonuses are awarded for on-time, consecutive payments. They are a % increase of the Base Credit Limit. They occur at payments 1, 3, 6, 9, 11, and every 3 payments afterward.">
                        <InfoCircleOutlined />
                    </Tooltip>
                </>
            ),
            dataIndex: 'incentiveBonusDollars',
            key: 'incentiveBonusDollars',
            render: (text) => formatToDollar(text),
        },
        {
            title: 'Annual Subscription',
            dataIndex: 'hasAnnualSubscription',
            key: 'hasAnnualSubscription',
            render: (text) => text ? 'Yes' : 'No',
        },
        {
            title: (
                <>
                    Payment Streak{' '}
                    <Tooltip title="The payment streak when this Credit Limit change was calculated. Warning, this may not match the current streak if in between bonus milestones.">
                        <InfoCircleOutlined />
                    </Tooltip>
                </>
            ),
            dataIndex: 'consecutiveCcPayments',
            key: 'consecutiveCcPayments',
        },
    ];

    const expandedRowRender = (row) => {
        return <CreditLimitIncentivesDetailColumns details={row} />
    };

    const isAnnual = creditLimitChanges[0]?.hasAnnualSubscription || false;

    return (
        <>
            <h3>
                Credit Limit Changes
                <Data isTag tagColor={domain?.domain?.creditLimitIncentivesEnabled ? 'success' : 'error'} title="Credit Limit Incentives Enabled" value={domain?.domain?.creditLimitIncentivesEnabled ? 'Yes' : 'No'} />
            </h3>
            
            <Table
                columns={columns}
                dataSource={creditLimitChanges}
                expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpandedRowsChange: setExpandedRowKeys,
                }}
                rowKey="id"
                pagination={{ pageSize: 5 }}
            />

            {isAnnual ? (
                <Descriptions title="Base Credit Limit Details" layout="vertical" column={4} bordered>
                    <Descriptions.Item label="Plaid Account ID" span={1}>
                        {baseCreditLimit?.plaidAccountId || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="UW Model" span={1}>
                        {baseCreditLimit?.model || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Base Credit Limit (Current)" span={1}>
                        {formatToDollar(baseCreditLimit?.annualPlanBaseLimitDollars) || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Base Credit Limit (If Downgraded to Monthly Plan)" span={1}>
                        {formatToDollar(baseCreditLimit?.monthlyPlanBaseLimitDollars) || 'N/A'}
                    </Descriptions.Item>
                </Descriptions>
            ) : (
                <Descriptions title="Base Credit Limit Summary" layout="vertical" column={4} bordered>
                    <Descriptions.Item label="Plaid Account ID" span={1}>
                        {baseCreditLimit?.plaidAccountId || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Underwriting Model" span={1}>
                        {baseCreditLimit?.model || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Base Credit Limit (Current)" span={1}>
                        {formatToDollar(baseCreditLimit?.monthlyPlanBaseLimitDollars) || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Base Credit Limit (if Upgraded to Annual Plan)" span={1}>
                        {formatToDollar(baseCreditLimit?.annualPlanBaseLimitDollars) || 'N/A'}
                    </Descriptions.Item>
                </Descriptions>
            )}
        </>
    );
}

// Extra Details Section to avoid the page getting too big.
function CreditLimitIncentivesDetailColumns({ details }) {
    return (
        <>
            <Descriptions layout="vertical" column={8} bordered>
                <Descriptions.Item label="Delinquent" span={2}>
                    {details?.isDelinquent ? 'Yes' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item label="Last Delinquency Date" span={2}>
                    { formatDate(details?.lastDateInDelinquency) || "N/A" }
                </Descriptions.Item>
                <Descriptions.Item label="Next Bonus Payment #" span={2}>
                    { details?.nextBonusPaymentMilestone}
                </Descriptions.Item>
                <Descriptions.Item label="Next Bonus Credit Limit" span={2}>
                    { formatToDollar(details?.nextBonusCreditLimitDollars) || "N/A" }
                </Descriptions.Item>
            </Descriptions>
        </>
    )
}


export default CreditLimitIncentivesComponent;
